import { selectCurrentHotelName } from 'accessors'
import { dripMediumOptionsWithEmail } from 'features/CustomCampaigns/EditCampaign/CampaignSetup'
import NodeConnector from 'features/CartAbandonmentFlow/NodeConnector'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import DripDelayNode from '../DripDelayNode'
import DripStepCard from '../DripStepCard'
import DripTriggerCard from '../DripTriggerCard'
import EditDripStepDialog from '../EditDripStepDialog'
import {
  convertFromHours,
  DRIP_SEQUENCE_STEP_IDS,
  getConditionLabel,
  getStepDialogTitle,
  isFollowUpStepDisabled,
  replaceSequenceTags
} from '../dripSequenceHelpers'
import styles from './DripSequenceFlow.module.scss'

function DripSequenceFlow({
  steps,
  onStepsChange,
  audienceName,
  audienceSize,
  campaignId,
  medium,
  stepErrors = {},
  onPersistDraft
}) {
  const userName = useSelector(s => s.login.currentUser.name)
  const hotelName = useSelector(selectCurrentHotelName)
  const isDemoMode = useSelector(s => s.login.isDemoMode)

  const [editingStepId, setEditingStepId] = useState(null)

  const step1 = steps.find(s => s.id === DRIP_SEQUENCE_STEP_IDS.step1)
  const step2 = steps.find(s => s.id === DRIP_SEQUENCE_STEP_IDS.step2)
  const step3 = steps.find(s => s.id === DRIP_SEQUENCE_STEP_IDS.step3)

  const isStep3Disabled = isFollowUpStepDisabled(steps, DRIP_SEQUENCE_STEP_IDS.step3)

  const handleStepClick = stepId => {
    setEditingStepId(stepId)
  }

  const handleDialogClose = () => {
    setEditingStepId(null)
  }

  const buildStepsWithDialogData = data =>
    steps.map(step => {
      if (step.id !== editingStepId) return step

      return {
        ...step,
        isActive: data.isEnabled,
        hoursAfterPreviousCampaign: data.hoursAfterPreviousCampaign,
        medium,
        messageTemplate: data.messageTemplate,
        messageMedia: data.messageMedia,
        hasEmailCampaign: dripMediumOptionsWithEmail.includes(medium)
      }
    })

  const handleStepSave = data => {
    onStepsChange(buildStepsWithDialogData(data))
    setEditingStepId(null)
  }

  const handlePersistDraftBeforeTest = onPersistDraft
    ? async data => {
        const updatedSteps = buildStepsWithDialogData(data)
        onStepsChange(updatedSteps)
        await onPersistDraft(updatedSteps)
      }
    : null

  const editingStep = editingStepId ? steps.find(s => s.id === editingStepId) : null
  const editingDelay = editingStep
    ? convertFromHours(editingStep.hoursAfterPreviousCampaign)
    : null

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h3 className={styles.title}>Message sequence</h3>
        <p className={styles.description}>
          Configure a multi-step drip with automatic follow-ups. Click any step to edit
          its delay, channels, and content.
        </p>
      </div>

      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.active}`} />
          Active step
        </div>
        <div className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.inactive}`} />
          Inactive step
        </div>
      </div>

      <div className={styles.scrollArea}>
      <DripTriggerCard audienceName={audienceName} audienceSize={audienceSize} />
      <NodeConnector className={styles.flowConnector} innerClassName={styles.flowConnectorInner} />

      <DripStepCard
        stepNumber={1}
        title={step1?.title || 'Initial message'}
        messagePreview={replaceSequenceTags(step1?.messageTemplate, {
          userName,
          hotelName,
          isDemoMode
        })}
        isActive={step1?.isActive}
        hasEmailCampaign={step1?.hasEmailCampaign}
        medium={medium}
        errorMessage={stepErrors[DRIP_SEQUENCE_STEP_IDS.step1]}
        isInitialStep={true}
        onClick={() => handleStepClick(DRIP_SEQUENCE_STEP_IDS.step1)}
      />

      <NodeConnector className={styles.flowConnector} innerClassName={styles.flowConnectorInner} />
      <DripDelayNode
        value={getConditionLabel(step2?.hoursAfterPreviousCampaign).value}
        unit={getConditionLabel(step2?.hoursAfterPreviousCampaign).unit}
        onClick={() => handleStepClick(DRIP_SEQUENCE_STEP_IDS.step2)}
      />
      <NodeConnector hasArrow className={styles.flowConnector} innerClassName={styles.flowConnectorInner} />

      <DripStepCard
        stepNumber={2}
        title={step2?.title || 'Follow-up 1'}
        messagePreview={replaceSequenceTags(step2?.messageTemplate, {
          userName,
          hotelName,
          isDemoMode
        })}
        isActive={step2?.isActive}
        hasEmailCampaign={step2?.hasEmailCampaign}
        medium={medium}
        errorMessage={stepErrors[DRIP_SEQUENCE_STEP_IDS.step2]}
        onClick={() => handleStepClick(DRIP_SEQUENCE_STEP_IDS.step2)}
      />

      <NodeConnector
        isDisabledByPrevious={isStep3Disabled}
        className={styles.flowConnector}
        innerClassName={styles.flowConnectorInner}
      />
      <DripDelayNode
        value={getConditionLabel(step3?.hoursAfterPreviousCampaign).value}
        unit={getConditionLabel(step3?.hoursAfterPreviousCampaign).unit}
        onClick={() => handleStepClick(DRIP_SEQUENCE_STEP_IDS.step3)}
        isDisabledByPrevious={isStep3Disabled}
      />
      <NodeConnector
        hasArrow
        isDisabledByPrevious={isStep3Disabled}
        className={styles.flowConnector}
        innerClassName={styles.flowConnectorInner}
      />

      <DripStepCard
        stepNumber={3}
        title={step3?.title || 'Follow-up 2'}
        messagePreview={replaceSequenceTags(step3?.messageTemplate, {
          userName,
          hotelName,
          isDemoMode
        })}
        isActive={step3?.isActive}
        hasEmailCampaign={step3?.hasEmailCampaign}
        medium={medium}
        errorMessage={stepErrors[DRIP_SEQUENCE_STEP_IDS.step3]}
        isDisabledByPrevious={isStep3Disabled}
        onClick={() => handleStepClick(DRIP_SEQUENCE_STEP_IDS.step3)}
      />
      </div>

      {editingStep && (
        <EditDripStepDialog
          key={editingStepId}
          title={getStepDialogTitle(editingStepId)}
          contentId={editingStep.contentId ?? campaignId}
          campaignId={campaignId}
          medium={medium}
          onClose={handleDialogClose}
          onSave={handleStepSave}
          onPersistDraft={handlePersistDraftBeforeTest}
          showConfigurationStep={!editingStep.isInitialStep}
          initialData={{
            isEnabled: editingStep.isActive,
            sendAfterValue: editingDelay?.displayValue,
            sendAfterUnit: editingDelay?.unit,
            hoursAfterPreviousCampaign: editingStep.hoursAfterPreviousCampaign,
            medium,
            messageTemplate: editingStep.messageTemplate,
            messageMedia: editingStep.messageMedia
          }}
        />
      )}
    </div>
  )
}

export default DripSequenceFlow
